import BoundingBox from './BoundingBox';


export type Piece = 'X' | 'O' | '';
export type Turn = 'X' | 'O';
export type Result = 'X' | 'O' | 'tie' | undefined;

export type TicTacToePlayers = {
  playerX: string | undefined,
  playerO: string | undefined
};

export type ServerTicTacToeGame = {
  id: string;
  occupantsByID: string[];
  boundingBox: BoundingBox;
  players: TicTacToePlayers;
  board: Piece[][];
  turn: Turn;
  winner: Result;
}

/**
 * Front end representation of a TicTacToeGame with the bare minimum fields and functionality
 * in order to properly render the board and keep track of players and game interaction.
 */
export default class TicTacToeGame {

  /** The unique identifier for this tic tac toe area * */
  public id: string;

  /** The list of IDs of players who are playing the game and players who are spectating */
  public occupantsByID: string[] = [];

  public players: TicTacToePlayers = { playerX: undefined, playerO: undefined };

  public board: Piece[][] = [['', '', ''], ['', '', ''], ['', '', '']];

  public turn: Turn = 'X';

  private _winner: Result;

  public boundingBox: BoundingBox;  

  constructor(id: string, location: BoundingBox) {
    this.id = id;
    this.boundingBox = location;
  }

  get winner() : Result {
    return this._winner;
  } 
  
  set winner(winner : Result) {
    this._winner = winner;
  }

  /**
   * Add the given player to this game. If there are already two players, add them as a sepcator.
   * @param occupant the id of the player to add to this game
   */
  addOccupant(occupant: string): void {
    if (this.numberOfPlayers() === 2) {
      this.occupantsByID.push(occupant);
    } else {
      this.addPlayer(occupant);
      this.occupantsByID.push(occupant);
    }
  }

  /**
   * How many players are currently in this game? 
   */
  numberOfPlayers() : number {
    const x = this.players.playerX;
    const o = this.players.playerO;
    if (x && o) {
      return 2;
    } if (x || o) {
      return 1;
    }
    return 0;
  }

  /**
   * Adds a player to this TicTacToeGame
   * If there's space for another player, they will automatically become a player
   * Do not set both players to the same player
   * @param newPlayer The new player to add to the game
   */
  private addPlayer(newPlayer: string): void {
    const xid = this.players.playerX;
    const oid = this.players.playerO;
    if (!this.players.playerX && newPlayer !== oid) {
      this.players.playerX = newPlayer;
    } else if (!this.players.playerO && newPlayer !== xid) {
      this.players.playerO = newPlayer;
    }
  }

  /**
   * Removes the given player from this TicTacToeGame
   * @param player Removes 
   */
  removePlayer(player: string): void {
    const xid = this.players.playerX;
    const oid = this.players.playerO;
    if (xid === player) {
      this.players.playerX = undefined;
      this._winner = 'O';
    } else if (oid === player) {
      this.players.playerO = undefined;
      this._winner = 'X';
    }
    this.occupantsByID.splice(this.occupantsByID.findIndex(id => id === player), 1);
  }

  /**
   * Converts this TicTacToeGame into a ServerTicTacToeGame for transport through the socket.
   * @returns A ServerTicTacToeGame representation of this TicTacToeGame
   */
  toServerTicTacToeGame(): ServerTicTacToeGame {
    return {
      id: this.id,
      occupantsByID: this.occupantsByID,
      boundingBox: this.boundingBox,
      players: this.players,
      board: this.board,
      turn: this.turn,
      winner: this.winner
    };
  }

  /**
   * Convert the given ServerTicTacToeGame into a TicTacToeGame.
   * @param serverGame the server game to convert into a TicTacToeGame
   * @returns A TicTacToeGame representation of this ServerTicTacToeGame
   */
  static fromServerTicTacToeGame(serverGame: ServerTicTacToeGame): TicTacToeGame {
    const ret = new TicTacToeGame(serverGame.id, BoundingBox.fromStruct(serverGame.boundingBox));
    ret.occupantsByID = serverGame.occupantsByID;
    ret.board = serverGame.board;
    ret._winner = serverGame.winner;
    ret.players = serverGame.players;
    ret.turn = serverGame.turn;
    return ret;
  }
}

